import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import ActionBar from "../../common/ActionBar";
import { PageWrapper, StyledLabel } from "./styles";
import LifeCycle from "../../common/LifeCycle/LifeCycle";
import CaseDetailsCard from "../../common/CaseDetails";
import CaseManager from "./common/CaseManager";
import BigBlueButton from "./common/BigBlueButton";
import _ from "lodash";
import { navigate } from "@reach/router";
import CaseService from "../../../services/CaseService";
import useLoader from "../../../hooks/useLoader";
import {
  RESPONDANT_ONBOARDED,
  CLAIMENT_STR,
  CLAIMANT_LIFE_CYCLE,
  NEGOTIATION_REACHED,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  CLAIMENT_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  AWAITING_RESPONDANT_QUIT,
  LIFE_CYCLE,
} from "../../../helpers/constants";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import NegotiationState from "./NegotiationState";
import DrawerTable from "../../internal/DrawerTable";
import PaymentModal from "./common/PaymentModal";
import AssignCaseMediator from "./common/AssignCaseMediatorModal";
import MediationState from "./MediationState";
import CaseCTA from "./common/CaseCTA";
import theme from "../../../assets/theme";
import AlertDialog from "../../common/Alert";
import queryString from "query-string";
import useNotification from "../../../hooks/useNotification";
import CopytoClipboard from "./common/CopytoClipboard.js";
import { CaseManagerCardData } from "./styles";
import { PrimaryCTAButton } from "../../common/Buttons";
import TranscriptionControl from "./common/TranscriptionControl";
import TranscriptionSettings from "../../internal/TranscriptionSettingsCases";
import { HyperLink } from "../../../styles/component/style";
import MeetingAccess from "./common/MeetingControl";
import RecordingControl from "./common/RecordingControl";
import COLORS from "../../../assets/Colors";
import styled from "styled-components";
import CaseDetailsStatus from "../../internal/CaseDetailsStatus";
import ExtraFieldsDrawer from "../../internal/DrawerTable/ExtraFieldsDrawer.js";
import RTMP from "./common/RTMP.js";
import GenerateGlobalMeeting from "../../internal/GlobalMeeting/index.js";

/* ─── Constants ──────────────────────────────────────────────────────────── */

const DISABLED_STATES = new Set([
  NEGOTIATION_REACHED,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  CLAIMENT_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  AWAITING_RESPONDANT_QUIT,
  "finalAward",
]);

const QUIT_STATES = new Set([
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  AWAITING_RESPONDANT_QUIT,
]);

const PARTY_PARAMS = `?${queryString.stringify({ page: 1, perPage: 1000 })}&sort=partyRole&sortDirection=asc`;
const MEDIATION_PARAMS = `?${queryString.stringify({ page: 1, perPage: 1000 })}`;

/* ─── Component ──────────────────────────────────────────────────────────── */

export default function CaseDetails({ id }) {
  const [state, setState] = useState();
  const [breadcrumbs, setBreadcrumbs] = useState(["Cases"]);
  const [caseDetails, setCaseDetails] = useState({});
  const [caesLifeCycle, setCaseLifeCyle] = useState([]);
  const [isRespondent, setIsRespondent] = useState(false);
  const [ownerId, setOwnerId] = useState();
  const [respondentId, setRespondentId] = useState();
  const [negotiationRounds, setNegotiationRounds] = useState({});
  const [currentState, setCurrentState] = useState({});
  const [openPaymentBox, setOpenPaymentBox] = useState(false);
  const [pendingRegistrationFee, setPendingRegistrationFee] = useState(false);
  const [openAssignCaseManager, setOpenAssignCaseManager] = useState(false);
  const [refreshDetails, setRefreshDetails] = useState(true);
  const [comments, setComments] = useState([]);
  const [dialogData, setDialogData] = useState({});
  const [open, setOpen] = useState(false);
  const [partyList, setPartyList] = useState([]);
  const [settingsModal, setSettingsModal] = useState(false);
  const [meetingDetail, setMeetingDetail] = useState();
  const [transcriptControl, settranscriptControl] = useState();
  const [refreshStatus, setRefreshStatus] = useState(true);
  const [statusModal, setStatusModal] = useState(false);
  const [docUploadModal, setDocUploadModal] = useState();
  const [fields, setFields] = useState(false);
  const [streamModal, setStreamModal] = useState(false);
  const [streamData, setStreamData] = useState("");
  const [publicModal, setPublicModal] = useState(false);

  const MTRef = useRef();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const { triggerNotify, setTriggerNotify } = useNotification();

  /* ── Helpers ── */

  const showError = useCallback(
    (error) => enqueueSnackbar(getErrorMessage(error), { variant: "error" }),
    [enqueueSnackbar],
  );

  const showSuccess = useCallback(
    (message) => enqueueSnackbar(message, { variant: "success" }),
    [enqueueSnackbar],
  );

  const closeDialog = useCallback(() => setOpen(false), []);

  const openDialog = useCallback((data) => {
    setDialogData(data);
    setOpen(true);
  }, []);

  const descriptionTextStyle = {
    textAlign: "left",
    fontFamily: theme.fonts.primaryFontSemiBold,
  };

  /* ── Notification refresh ── */

  useEffect(() => {
    if (triggerNotify) {
      setRefreshDetails(true);
      setTriggerNotify(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNotify]);

  /* ── Meeting / transcription access ── */

  useEffect(() => {
    if (!id || !refreshStatus) return;

    async function fetchMeetingAccess() {
      try {
        const data = await CaseService.GetMeetingAccesscases(id);
        setMeetingDetail(data);
        settranscriptControl(data);
      } catch (error) {
        showError(error);
      }
    }

    fetchMeetingAccess();
    setRefreshStatus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshStatus]);

  /* ── Case data fetch ── */

  useEffect(() => {
    if (!id || !refreshDetails) return;

    async function fetchPartyList() {
      try {
        const res = await CaseService.getPartyListByCaseId(id, PARTY_PARAMS);
        if (res?.data) setPartyList(res.data);
      } catch (error) {
        showError(error);
      }
    }

    async function fetchMediations() {
      setLoader({ state: true, message: "Fetching case details..." });
      try {
        const res = await CaseService.mediations(id, MEDIATION_PARAMS);
        if (res) setComments(res.data);
      } catch (error) {
        showError(error);
      } finally {
        setLoader({ state: false });
      }
    }

    async function fetchNegotiations() {
      setLoader({ state: true, message: "Fetching case details..." });
      try {
        const res = await CaseService.negotiations(id);
        setNegotiationRounds(res);
      } catch (error) {
        showError(error);
      } finally {
        setLoader({ state: false });
      }
    }

    async function fetchCase() {
      setLoader({ state: true, message: "Fetching case details..." });
      try {
        const res = await CaseService.getCase(id);
        if (!res) return;

        const caseRes = { ...res };

        if (caseRes.status !== "draft") {
          if (caseRes.status === "pendingRegistrationFee") {
            setPendingRegistrationFee(true);
          }
          if (!caseRes.respondentPartyId) {
            // status stays as-is
          } else if (
            caseRes.respondentStatus === "pending" ||
            caseRes.respondentStatus === "declined"
          ) {
            caseRes.status = RESPONDANT_ONBOARDED;
          } else if (
            caseRes.status === NEGOTIATION_REACHED &&
            caseRes.subscriptionKind === "adhoc"
          ) {
            caseRes.status = BOTH_PAID_NEGOTIATION;
          }
        }

        if (caseRes.resolutionKind === "negotiation") {
          fetchNegotiations();
        } else {
          fetchMediations();
        }

        fetchPartyList();
        setCaseDetails(caseRes);
        setBreadcrumbs([
          "Cases",
          _.startCase(caseRes.resolutionKind),
          caseRes.title,
        ]);
        setCaseLifeCyle(
          caseRes.resolutionKind !== "negotiation"
            ? JSON.parse(JSON.stringify(CLAIMANT_LIFE_CYCLE))
            : JSON.parse(JSON.stringify(LIFE_CYCLE)),
        );
        setIsRespondent(caseRes.agentRole !== CLAIMENT_STR);
        setOwnerId(caseRes.claimantPartyId);
        setRespondentId(caseRes.respondentPartyId);
      } catch (error) {
        showError(error);
        navigate("/dashboard/case-manager/negotiation");
      } finally {
        setLoader({ state: false });
        window.scrollTo(0, 0);
      }
    }

    fetchCase();
    setRefreshDetails(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshDetails]);

  /* ── Derive currentState from lifecycle ── */

  useEffect(() => {
    if (!caesLifeCycle.length || _.isEmpty(caseDetails)) return;

    if (caesLifeCycle.some((el) => el.key === caseDetails.status)) {
      setCurrentState(
        caesLifeCycle.find((el) => el.key === caseDetails.status),
      );
    } else if (!isRespondent) {
      const idx = caseDetails.respondentId ? 2 : 1;
      setCurrentState(caesLifeCycle.find((el) => el.index === idx));
    } else if (caseDetails.status === "finalAward") {
      setCurrentState({
        label: "Final Award",
        key: "finalAward",
        index: 5,
        enableSelection: false,
        cta: [],
      });
    }
  }, [caesLifeCycle, caseDetails, isRespondent]);

  /* ── Navigation ── */

  const onBreadcrumbClick = useCallback(
    (selected) => {
      const key = _.snakeCase(selected);
      if (key === _.snakeCase(caseDetails?.resolutionKind) || key === "cases") {
        navigate(`/dashboard/case-manager/${caseDetails?.resolutionKind}`);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseDetails?.resolutionKind],
  );

  /* ── Async actions ── */

  async function callMediationSucess() {
    setLoader({ state: true, message: "Updating case details..." });
    try {
      const res = await CaseService.mediationSuccess(id);
      if (res?.message) showSuccess(res.message);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
      setRefreshDetails(true);
    }
  }

  async function callQuitCase(reasonText, dateOfClose) {
    setLoader({ state: true, message: "Updating case details..." });
    try {
      const payload = { reasonForQuit: reasonText, dateOfClose };
      const res = await CaseService.quitMediation(id, payload);
      if (res?.message) {
        showSuccess(res.message);
        navigate(`/dashboard/case-manager/${caseDetails?.resolutionKind}`);
      }
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
    }
  }

  async function callQuitNegotiationCase(reasonText, dateOfClose) {
    setLoader({ state: true, message: "Updating case details..." });
    try {
      const payload = { reasonForQuit: reasonText, dateOfClose };
      const res = await CaseService.quitNegotiation(id, payload);
      if (res?.message) {
        showSuccess(res.message);
        navigate(`/dashboard/case-manager/${caseDetails?.resolutionKind}`);
      }
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
    }
  }

  async function callReopenCase() {
    setLoader({ state: true, message: "Updating case details..." });
    try {
      const res = await CaseService.updateCaseReopen(id);
      if (res?.message) showSuccess(res.message);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
      setRefreshDetails(true);
    }
  }

  async function callRemoveMediator(mediator) {
    setLoader({ state: true, message: "Removing mediator..." });
    const payload = {
      requestedMediators: caseDetails?.requestedMediators?.filter(
        (el) => el?.email !== mediator?.email,
      ),
    };
    try {
      const res = await CaseService.updateCase(id, payload);
      if (res?.message) showSuccess(res.message);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
      setRefreshDetails(true);
    }
  }

  async function presidingArbitrator(mediator) {
    const payload = {
      presidingArbitrator:
        mediator?.presidingArbitrator === "no" || !mediator?.presidingArbitrator
          ? "yes"
          : "no",
    };
    try {
      const res = await CaseService.presidingArbitrator(
        id,
        mediator?.id,
        payload,
      );
      if (res?.message) showSuccess(res.message);
    } catch (error) {
      showError(error);
    } finally {
      setRefreshDetails(true);
    }
  }

  async function getMeetingjoin(id) {
    try {
      const url = await CaseService.getMeeting(id);
      window.open(url);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
    }
  }

  async function stopTranscription(id) {
    setLoader({ state: true, message: "Update Meeting Status..." });
    try {
      const res = await CaseService.stopTranscription(id);
      if (res?.message) showSuccess(res.message);
      setRefreshStatus(true);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
    }
  }

  async function startTranscription(id) {
    setLoader({ state: true, message: "Start transcription..." });
    try {
      const res = await CaseService.startTranscription(id);
      if (res?.message) showSuccess(res.message);
      setTimeout(() => setRefreshStatus(true), 5000);
    } catch (error) {
      showError(error);
    } finally {
      setLoader({ state: false });
      setStreamModal(false);
    }
  }

  async function pushCaseToArbitration(id) {
    try {
      const res = await CaseService.pushCaseToArbitration(id);
      if (res?.message) showSuccess(res.message);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
    }
  }

  async function generateRTMPLink(meetingId) {
    setLoader({ state: true, message: "fetching rtmp..." });
    try {
      const res = await CaseService.generateRTMPLink(meetingId);
      if (res?.message) showSuccess(res.message);
      setStreamData(res);
    } catch (error) {
      showError(error);
    } finally {
      closeDialog();
      setLoader({ state: false });
      setStreamModal(true);
    }
  }

  /* ── Dialog builders ── */

  const onbuttonClick = useCallback(
    (type) => {
      const isNegotiation = caseDetails?.resolutionKind === "negotiation";
      const kind = _.capitalize(caseDetails?.resolutionKind);

      const configs = {
        MEDIATION_SUCCESS: {
          primaryBtnText: "Cancel",
          secondaryBtnText: "Proceed",
          clickSecondarybtn: callMediationSucess,
          desc: `Congratulations, your ${caseDetails?.resolutionKind} has been a success. Are you sure you want to proceed?`,
          heading: `${kind} Success`,
          buttonType: type,
        },
        QUIT: {
          primaryBtnText: "Cancel",
          secondaryBtnText: "Close",
          clickSecondarybtn: (reasonText, dateOfClose) =>
            isNegotiation
              ? callQuitNegotiationCase(reasonText, dateOfClose)
              : callQuitCase(reasonText, dateOfClose),
          desc: `Please note once you close the case, you cannot continue your ${caseDetails?.resolutionKind}.`,
          heading: "Do you really want to Close your case?",
          buttonType: type,
          status: true,
        },
        REOPEN_CASE: {
          primaryBtnText: "Cancel",
          secondaryBtnText: "Proceed",
          clickSecondarybtn: callReopenCase,
          desc: "Click proceed to Reopen your case, Click cancel to Cancel your action",
          heading: "Do you really want to Reopen your case?",
          buttonType: type,
        },
      };

      const config = configs[type];
      if (!config) return;

      openDialog({
        ...config,
        clickPrimaryBtn: closeDialog,
        onDialogClose: closeDialog,
        descriptionTextStyle,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseDetails?.resolutionKind],
  );

  const removeMediator = useCallback(
    (mediator) => {
      const isMediation = caseDetails?.resolutionKind === "mediation";
      openDialog({
        primaryBtnText: "No",
        secondaryBtnText: "Yes",
        clickSecondarybtn: () => callRemoveMediator(mediator),
        clickPrimaryBtn: closeDialog,
        onDialogClose: closeDialog,
        desc: `Are you sure you want to remove this ${isMediation ? "mediator" : "arbitrator"}?`,
        heading: `Remove ${isMediation ? "Mediator" : "Arbitrator"}`,
        descriptionTextStyle,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseDetails?.resolutionKind],
  );

  const stopTranscriptionAlert = useCallback(
    (id) => {
      openDialog({
        primaryBtnText: "No",
        secondaryBtnText: "Yes",
        clickSecondarybtn: () => stopTranscription(id),
        clickPrimaryBtn: closeDialog,
        onDialogClose: closeDialog,
        desc: (
          <>
            After you have stopped this live transcription session your
            transcript will be moved to the case documents section. Please
            ensure you have saved your transcription changes.
            <br />
            <br />
            <b style={{ fontSize: "18px" }}>
              Are you sure you want to stop the transcription
            </b>
            ?
          </>
        ),
        heading: "Stop Transcription",
        descriptionTextStyle,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const joinMeetingAlert = useCallback(
    (id, resolutionKind) => {
      openDialog({
        primaryBtnText: "Proceed",
        secondaryBtnText: "Cancel",
        clickSecondarybtn: closeDialog,
        clickPrimaryBtn: () => getMeetingjoin(id),
        onDialogClose: closeDialog,
        desc: (
          <b style={{ color: COLORS.LOGOUT_RED }}>
            We may record the meeting for case proceedings and transcription
            purposes
          </b>
        ),
        heading: `Join ${_.capitalize(resolutionKind)} Session`,
        descriptionTextStyle,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const pushCaseToArbitrationAlert = useCallback(
    (id) => {
      openDialog({
        primaryBtnText: "Proceed",
        secondaryBtnText: "Cancel",
        clickSecondarybtn: closeDialog,
        clickPrimaryBtn: () => pushCaseToArbitration(id),
        onDialogClose: closeDialog,
        desc: <b>Are you sure you want to move case to arbitration?</b>,
        heading: "Move to Arbitration",
        descriptionTextStyle,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /* ── Derived values ── */

  const isActionsDisabled = DISABLED_STATES.has(currentState?.key);
  const isNonNegotiation = caseDetails?.resolutionKind !== "negotiation";
  const isNotDraft = caseDetails?.status !== "draft";
  const mediatorTitle =
    caseDetails?.resolutionKind === "mediation" ? "Mediator" : "Arbitrator";
  const meetingOpen =
    meetingDetail?.recordingStatus === "yes" &&
    meetingDetail?.transcriptionStatus === "yes" &&
    meetingDetail?.status === "open";

  /* ── Render ── */

  return (
    <Fragment>
      <ActionBar
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={onBreadcrumbClick}
      />

      <PageWrapper>
        {/* Case Details Card */}
        <CaseDetailsCard
          isActionsDisabled={isActionsDisabled}
          disableRefund={
            pendingRegistrationFee || caseDetails?.status === "draft"
          }
          setOpenPaymentBox={setOpenPaymentBox}
          caseDetails={caseDetails}
          refreshDetails={refreshDetails}
          setRefreshDetails={setRefreshDetails}
          pendingRegistrationFee={pendingRegistrationFee}
          {...{
            setState,
            id,
            MTRef,
            partyList,
            docUploadModal,
            setDocUploadModal,
            setFields,
          }}
        />

        {/* Case Managers & Action Controls */}
        <ControlsRow>
          <ControlsGroup>
            {/* Primary case manager */}
            {caseDetails?.caseManager && (
              <CardSlot>
                <CaseManager
                  caseManager={caseDetails.caseManager}
                  name={caseDetails.caseManager.name}
                  email={caseDetails.caseManager.email}
                  mobile={caseDetails.caseManager.mobile}
                  avatarUrl={caseDetails.caseManager.avatarUrl}
                  showRemove={false}
                />
              </CardSlot>
            )}

            {/* Co-managers (arbitration only) */}
            {/* {caseDetails?.resolutionKind === "arbitration" &&
              caseDetails?.caseManagerIds
                ?.filter((cm) => cm?.id !== caseDetails?.caseManager?.id)
                ?.map((cm, idx) => (
                  <CardSlot key={idx}>
                    <CaseManager
                      caseManager={cm}
                      name={cm.name}
                      email={cm.email}
                      mobile={cm.mobile}
                      avatarUrl={cm.avatarUrl}
                      showRemove={false}
                      title="Associate Scrutiny Clerk"
                    />
                  </CardSlot>
                ))} */}

                {/* Associate Scrutiny Clerk (Arbitration & Mediation) */}
          {["arbitration", "mediation"].includes(caseDetails?.resolutionKind) &&
            caseDetails?.caseManagerIds
              ?.filter((cm) => cm?.id !== caseDetails?.caseManager?.id)
              ?.map((cm, idx) => (
                <CardSlot key={cm.id || idx}>
                  <CaseManager
                    caseManager={cm}
                    name={cm.name}
                    email={cm.email}
                    mobile={cm.mobile}
                    avatarUrl={cm.avatarUrl}
                    showRemove={false}
                    title="Associate Scrutiny Clerk"
                  />
                </CardSlot>
              ))}

                

            {/* Mediators / Arbitrators */}
            {isNonNegotiation && (
              <>
                {caseDetails?.requestedMediators?.map((mediator, idx) => (
                  <CardSlot key={idx}>
                    <CaseManager
                      disabled={false}
                      caseManager={mediator}
                      title={mediatorTitle}
                      removeMediator={() => removeMediator(mediator)}
                      presidingArbitrator={() => presidingArbitrator(mediator)}
                      mediator={mediator}
                      caseDetails={caseDetails}
                      addMediator={() => setOpenAssignCaseManager(true)}
                      showRemove={true}
                      adhocPending={!mediator?.id}
                    />
                  </CardSlot>
                ))}

                {(!caseDetails?.requestedMediators?.length ||
                  caseDetails?.resolutionKind === "arbitration") && (
                  <CardSlot>
                    <CaseManager
                      disabled={false}
                      title={mediatorTitle}
                      addMediator={() => setOpenAssignCaseManager(true)}
                    />
                  </CardSlot>
                )}
              </>
            )}

            {/* Join session */}
            {isNonNegotiation && (
              <CardSlot>
                <BigBlueButton
                  onClick={() =>
                    joinMeetingAlert(id, caseDetails?.resolutionKind)
                  }
                  type={`Join ${_.capitalize(caseDetails?.resolutionKind)} Session`}
                />
              </CardSlot>
            )}

            {/* Move to Arbitration */}
            {caseDetails?.resolutionKind === "mediation" && (
              <CardSlot>
                <CaseManagerCardData>
                  <PrimaryCTAButton
                    onClick={() => pushCaseToArbitrationAlert(id)}
                  >
                    Move to Arbitration
                  </PrimaryCTAButton>
                </CaseManagerCardData>
              </CardSlot>
            )}

            {/* Transcription & Cross-Examination — only when meeting is open */}
            {meetingOpen && isNonNegotiation && (
              <>
                <CardSlot>
                  <CaseManagerCardData>
                    <PrimaryCTAButton
                      style={{ width: "295px" }}
                      onClick={() =>
                        navigate(
                          meetingDetail?.crossExamination
                            ? `/dashboard/case-manager/${caseDetails?.resolutionKind}/${id}/cross-examination`
                            : `/dashboard/case-manager/${caseDetails?.resolutionKind}/${id}/livetranscription`,
                          {
                            state: meetingDetail?.crossExamination
                              ? meetingDetail
                              : {
                                  ...caseDetails,
                                  interim_results:
                                    meetingDetail?.transcriptionModelParams
                                      ?.interim_results,
                                },
                          },
                        )
                      }
                    >
                      {meetingDetail?.crossExamination
                        ? "Cross Examination"
                        : "Live Transcription"}
                    </PrimaryCTAButton>
                  </CaseManagerCardData>
                </CardSlot>

                <CardSlot>
                  <CaseManagerCardData>
                    {meetingDetail?.transcriptionRunningStatus ? (
                      <PrimaryCTAButton
                        style={{
                          backgroundColor: "#D04848",
                          border: "1px solid #D04848",
                        }}
                        onClick={() => stopTranscriptionAlert(id)}
                      >
                        Stop Transcription
                      </PrimaryCTAButton>
                    ) : (
                      <PrimaryCTAButton
                        onClick={() =>
                          meetingDetail?.isLocalTranscriptionServer === 4
                            ? generateRTMPLink(meetingDetail?.meetingId)
                            : startTranscription(id)
                        }
                      >
                        Start Transcription
                      </PrimaryCTAButton>
                    )}
                  </CaseManagerCardData>
                </CardSlot>
              </>
            )}

            {/* Meeting access + recording controls */}
            {isNonNegotiation && (
              <CardSlot>
                <MeetingAccess
                  {...{
                    state,
                    setState,
                    id,
                    MTRef,
                    meetingDetail,
                    setRefreshStatus,
                    caseDetails,
                  }}
                />
                <RecordingControl
                  {...{
                    state,
                    setState,
                    id,
                    MTRef,
                    meetingDetail,
                    caseDetails,
                    setRefreshStatus,
                  }}
                />
              </CardSlot>
            )}

            {/* Transcription control */}
            {isNonNegotiation && isNotDraft && (
              <CardSlot>
                <TranscriptionControl
                  {...{
                    state,
                    setState,
                    id,
                    MTRef,
                    setRefreshStatus,
                    transcriptControl,
                    settranscriptControl,
                  }}
                />
              </CardSlot>
            )}

            {/* Hyperlinks */}
            {isNonNegotiation && isNotDraft && (
              <>
                <LinkSlot>
                  <HyperLink onClick={() => setSettingsModal(true)}>
                    Transcription Settings
                  </HyperLink>
                </LinkSlot>
                <LinkSlot>
                  <HyperLink onClick={() => setPublicModal(true)}>
                    Generate Public Link
                  </HyperLink>
                </LinkSlot>
              </>
            )}
          </ControlsGroup>
        </ControlsRow>

        {/* Copy to clipboard */}
        {isNonNegotiation && (
          <CardSlot>
            <CopytoClipboard id={id} />
          </CardSlot>
        )}

        {/* Lifecycle, state content, quit info, CTAs */}
        {isNotDraft && (
          <>
            <Section>
              <StyledLabel>Case Lifecycle</StyledLabel>
              <LifeCycle
                items={caesLifeCycle}
                resolutionKind={caseDetails?.resolutionKind}
                selectedIndex={currentState?.index}
                isMediation={isNonNegotiation}
                status={currentState?.text}
                currentStateKey={currentState?.key}
                {...{ setStatusModal }}
              />
            </Section>

            <Section>
              {caseDetails?.resolutionKind === "negotiation" ? (
                <NegotiationState
                  {...{
                    currentState,
                    negotiationRounds,
                    isRespondent,
                    ownerId,
                    respondentId,
                    caseDetails,
                  }}
                />
              ) : (
                <MediationState
                  {...{
                    currentState,
                    negotiationRounds,
                    isRespondent,
                    ownerId,
                    respondentId,
                    caseDetails,
                    comments,
                    setComments,
                    partyList,
                  }}
                  isLocalTranscriptionServer={
                    meetingDetail?.isLocalTranscriptionServer
                  }
                />
              )}
            </Section>

            {QUIT_STATES.has(currentState?.key) && (
              <QuitWrapper>
                <QuitCard>
                  <QuitLabel>Reason for Closing</QuitLabel>
                  <QuitValue>{caseDetails?.reasonForClose || "-"}</QuitValue>
                </QuitCard>

                <QuitCard>
                  <QuitLabel>Date of Close</QuitLabel>
                  <QuitValue>{caseDetails?.dateofClose || "-"}</QuitValue>
                </QuitCard>
              </QuitWrapper>
            )}

            <Section>
              <CaseCTA
                cta={
                  isNonNegotiation
                    ? currentState.cta
                    : currentState?.cta?.filter(
                        (c) => c.type !== "MEDIATION_SUCCESS",
                      )
                }
                resolutionKind={caseDetails?.resolutionKind}
                disableButton={false}
                onbuttonClick={onbuttonClick}
              />
            </Section>
          </>
        )}
      </PageWrapper>

      {/* Modals & Drawers */}
      <AlertDialog
        isOpen={open}
        {...dialogData}
        descriptionTextStyle={descriptionTextStyle}
      />

      <DrawerTable
        {...{ state, setState, id, MTRef, docUploadModal, caseDetails }}
        title={caseDetails?.title}
        type={caseDetails?.resolutionKind}
        claimantId={caseDetails?.claimantParty?.ownerId}
        respondentId={caseDetails?.respondentParty?.ownerId}
        agreementsAndAwards={caseDetails?.agreementsAndAwards}
        isActionsDisabled={isActionsDisabled}
      />

      <ExtraFieldsDrawer {...{ fields, setFields, caseDetails, id }} />

      {pendingRegistrationFee && (
        <PaymentModal
          modal={openPaymentBox}
          setModal={() => {
            setOpenPaymentBox(false);
            setRefreshDetails(true);
          }}
          id={caseDetails?.id}
        />
      )}

      <AssignCaseMediator
        modal={openAssignCaseManager}
        setModal={setOpenAssignCaseManager}
        type={mediatorTitle}
        onAssignSuccess={() => {
          setRefreshDetails(true);
          setOpenAssignCaseManager(false);
        }}
        caseDetails={caseDetails}
      />

      <TranscriptionSettings
        modal={settingsModal}
        setModal={setSettingsModal}
        {...{ setRefreshStatus, meetingDetail, setMeetingDetail, id }}
      />

      <CaseDetailsStatus
        currentState={currentState}
        {...{ statusModal, setStatusModal, setRefreshDetails, id, caseDetails }}
      />

      {streamModal && (
        <RTMP
          {...{ streamModal, setStreamModal, streamData, id }}
          handleStart={startTranscription}
        />
      )}

      {publicModal && (
        <GenerateGlobalMeeting
          {...{ publicModal, setPublicModal, id }}
          resolutionKind={caseDetails?.resolutionKind}
          title={caseDetails?.title}
        />
      )}
    </Fragment>
  );
}

/* ─── Styled Components ──────────────────────────────────────────────────── */

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ControlsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const CardSlot = styled.div`
  margin-right: 25px;
  margin-top: 28px;
`;

const LinkSlot = styled.div`
  margin-right: 25px;
  margin-top: 32px;
`;

const Section = styled.div`
  margin-top: 28px;
`;

export const ReasonForQuit = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  color: ${COLORS.LOGOUT_RED};
`;

export const ReasonForQuitText = styled.p`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  margin-left: 4px;
`;

export const QuitContainer = styled.div`
  background-color: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.COLOR_DARK};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  margin-top: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease-in-out;
  max-width: 100%;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 18px;
    border-radius: 14px;
  }

  @media (min-width: 768px) {
    max-width: 85%;
  }
`;

export const QuitContents = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  word-break: break-word;

  strong,
  b {
    font-family: ${theme.fonts.primaryFontExtraBold};
    color: inherit;
  }

  @media (max-width: 480px) {
    font-size: 15.5px;
  }
`;

export const QuitWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 24px;

  @media ${theme.breakpoints.sm_up} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const QuitCard = styled.div`
  padding: 16px 18px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  }
`;

export const QuitLabel = styled.div`
  font-size: 13px;
  color: ${COLORS.LOGOUT_RED};
  margin-bottom: 6px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const QuitValue = styled.div`
  font-size: 15px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  line-height: 1.5;
  word-break: break-word;

  & strong {
    font-family: ${theme.fonts.primaryFontExtraBold};
  }
`;
